<script lang="ts" setup>
import { ref, computed, watch, nextTick } from "vue";
import { Modal } from "bootstrap";

interface ServiceData {
  label: string;
  description?: string;
  serviceType?: string;
  [key: string]: any;
}

interface InputField {
  key: string;
  label: string;
  type: string;
  required?: boolean;
  placeholder?: string;
  options?: string[];
  min?: number;
}

const props = defineProps<{
  show: boolean;
  serviceData: ServiceData | null;
  nodeId: string | null;
}>();

const emit = defineEmits<{
  hide: [];
  cancel: [];
  execute: [nodeId: string, inputs: Record<string, any>];
}>();

const modalRef = ref<HTMLElement>();
const modalInstance = ref<Modal>();
const isExecuting = ref(false);
const inputValues = ref<Record<string, any>>({});

// Initialize modal when component mounts
watch(
  () => props.show,
  async (show) => {
    if (show && modalRef.value && !modalInstance.value) {
      modalInstance.value = new Modal(modalRef.value);
    }

    if (show && modalInstance.value) {
      resetForm();
      modalInstance.value.show();
    } else if (!show && modalInstance.value) {
      modalInstance.value.hide();
    }
  },
  { immediate: true }
);

// Service-specific input fields configuration
const inputFields = computed((): InputField[] => {
  if (!props.serviceData) return [];

  const serviceType =
    props.serviceData.serviceType || props.serviceData.label.toLowerCase();

  // Define input fields based on service type
  if (serviceType.includes("email") || serviceType.includes("send email")) {
    return [
      {
        key: "to",
        label: "To",
        type: "email",
        required: true,
        placeholder: "recipient@example.com",
      },
      {
        key: "subject",
        label: "Subject",
        type: "text",
        required: true,
        placeholder: "Email subject",
      },
      {
        key: "body",
        label: "Message",
        type: "textarea",
        required: true,
        placeholder: "Email content...",
      },
    ];
  }

  if (serviceType.includes("api") || serviceType.includes("trigger")) {
    return [
      {
        key: "url",
        label: "URL",
        type: "url",
        required: true,
        placeholder: "https://api.example.com/endpoint",
      },
      {
        key: "method",
        label: "Method",
        type: "select",
        required: true,
        options: ["GET", "POST", "PUT", "DELETE"],
      },
      {
        key: "headers",
        label: "Headers (JSON)",
        type: "textarea",
        placeholder: '{"Authorization": "Bearer token"}',
      },
      {
        key: "body",
        label: "Request Body (JSON)",
        type: "textarea",
        placeholder: '{"key": "value"}',
      },
    ];
  }

  if (serviceType.includes("delay") || serviceType.includes("wait")) {
    return [
      {
        key: "duration",
        label: "Duration (seconds)",
        type: "number",
        required: true,
        placeholder: "5",
        min: 1,
      },
      {
        key: "message",
        label: "Wait Message",
        type: "text",
        placeholder: "Waiting for process...",
      },
    ];
  }

  if (serviceType.includes("condition") || serviceType.includes("if")) {
    return [
      {
        key: "leftValue",
        label: "Left Value",
        type: "text",
        required: true,
        placeholder: "Variable or value",
      },
      {
        key: "operator",
        label: "Operator",
        type: "select",
        required: true,
        options: [
          "equals",
          "not_equals",
          "greater_than",
          "less_than",
          "contains",
        ],
      },
      {
        key: "rightValue",
        label: "Right Value",
        type: "text",
        required: true,
        placeholder: "Value to compare",
      },
    ];
  }

  if (serviceType.includes("database") || serviceType.includes("data")) {
    return [
      {
        key: "query",
        label: "Query",
        type: "textarea",
        required: true,
        placeholder: "SELECT * FROM users WHERE id = ?",
      },
      {
        key: "parameters",
        label: "Parameters (JSON Array)",
        type: "textarea",
        placeholder: '[1, "john@example.com"]',
      },
    ];
  }

  if (serviceType.includes("webhook")) {
    return [
      {
        key: "url",
        label: "Webhook URL",
        type: "url",
        required: true,
        placeholder: "https://hooks.example.com/webhook",
      },
      {
        key: "payload",
        label: "Payload (JSON)",
        type: "textarea",
        required: true,
        placeholder: '{"event": "test", "data": {}}',
      },
    ];
  }

  if (serviceType.includes("notification") || serviceType.includes("alert")) {
    return [
      {
        key: "title",
        label: "Title",
        type: "text",
        required: true,
        placeholder: "Notification title",
      },
      {
        key: "message",
        label: "Message",
        type: "textarea",
        required: true,
        placeholder: "Notification message...",
      },
      {
        key: "priority",
        label: "Priority",
        type: "select",
        options: ["low", "normal", "high", "urgent"],
      },
    ];
  }

  // Default generic inputs
  return [
    {
      key: "input",
      label: "Input Data",
      type: "textarea",
      placeholder: "Enter input data for this service...",
    },
    {
      key: "parameters",
      label: "Parameters (JSON)",
      type: "textarea",
      placeholder: '{"key": "value"}',
    },
  ];
});

function resetForm() {
  inputValues.value = {};
  // Set default values
  inputFields.value.forEach((field) => {
    if (field.options && field.options.length > 0) {
      inputValues.value[field.key] = field.options[0];
    }
  });
}

function handleSubmit() {
  if (!props.nodeId) return;

  // Validate required fields
  const missingFields = inputFields.value
    .filter((field) => field.required && !inputValues.value[field.key])
    .map((field) => field.label);

  if (missingFields.length > 0) {
    alert(`Please fill in required fields: ${missingFields.join(", ")}`);
    return;
  }

  // Parse JSON fields
  const processedInputs = { ...inputValues.value };
  inputFields.value.forEach((field) => {
    if (
      ["headers", "body", "parameters", "payload"].includes(field.key) &&
      processedInputs[field.key]
    ) {
      try {
        processedInputs[field.key] = JSON.parse(processedInputs[field.key]);
      } catch (e) {
        // Keep as string if JSON parsing fails
      }
    }
  });

  isExecuting.value = true;
  emit("execute", props.nodeId, processedInputs);

  // Reset executing state after a delay (will be managed by parent)
  setTimeout(() => {
    isExecuting.value = false;
    emit("hide");
  }, 1000);
}

function handleClose() {
  emit("cancel");
  emit("hide");
}
</script>

<template>
  <div
    ref="modalRef"
    class="modal fade"
    tabindex="-1"
    @hidden.bs.modal="handleClose"
  >
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title">
            <i class="bi bi-play-circle me-2"></i>
            Run Service: {{ serviceData?.label }}
          </h2>
          <button type="button" class="btn-close" @click="handleClose"></button>
        </div>

        <div class="modal-body">
          <div v-if="serviceData?.description" class="alert alert-info">
            <i class="bi bi-info-circle me-2"></i>
            {{ serviceData.description }}
          </div>

          <form @submit.prevent="handleSubmit">
            <div v-for="field in inputFields" :key="field.key" class="mb-3">
              <label :for="field.key" class="form-label">
                {{ field.label }}
                <span v-if="field.required" class="text-danger">*</span>
              </label>

              <!-- Text/Email/URL inputs -->
              <input
                v-if="['text', 'email', 'url'].includes(field.type)"
                :id="field.key"
                v-model="inputValues[field.key]"
                :type="field.type"
                class="form-control"
                :placeholder="field.placeholder"
                :required="field.required"
              />

              <!-- Number input -->
              <input
                v-else-if="field.type === 'number'"
                :id="field.key"
                v-model.number="inputValues[field.key]"
                type="number"
                class="form-control"
                :placeholder="field.placeholder"
                :required="field.required"
                :min="field.min || 0"
              />

              <!-- Select dropdown -->
              <select
                v-else-if="field.type === 'select'"
                :id="field.key"
                v-model="inputValues[field.key]"
                class="form-select"
                :required="field.required"
              >
                <option
                  v-for="option in field.options || []"
                  :key="option"
                  :value="option"
                >
                  {{ option.charAt(0).toUpperCase() + option.slice(1) }}
                </option>
              </select>

              <!-- Textarea -->
              <textarea
                v-else-if="field.type === 'textarea'"
                :id="field.key"
                v-model="inputValues[field.key]"
                class="form-control"
                :placeholder="field.placeholder"
                :required="field.required"
                rows="3"
              ></textarea>
            </div>
          </form>
        </div>

        <div class="modal-footer">
          <button type="button" class="bb-btn-cancel" @click="handleClose">
            Cancel
          </button>
          <button
            type="button"
            class="bb-btn-primary-cta"
            :disabled="isExecuting"
            @click="handleSubmit"
          >
            <span
              v-if="isExecuting"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            <i v-else class="bi bi-play-fill me-2"></i>
            {{ isExecuting ? "Executing..." : "Run Service" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.modal-dialog {
  max-width: 600px;
}

.form-label {
  font-weight: 500;
}

.alert {
  margin-bottom: 1.5rem;
}

textarea.form-control {
  resize: vertical;
  min-height: 80px;
}

.btn:disabled {
  pointer-events: none;
}
</style>
